<template>
  <div class="video-upload-component">
    <!-- 上传控制区 -->
    <el-card shadow="never" class="upload-control-card">
      <div class="upload-header">
        <h3>
          <i class="el-icon-video-camera"></i>
          批量视频上传
        </h3>
        <div class="upload-stats" v-if="totalVideos > 0">
          <el-tag type="info" size="medium">
            总计: {{ totalVideos }}
          </el-tag>
          <el-tag
              type="warning"
              size="medium"              style="margin-left: 8px"
              v-if="pendingCount > 0"
          >
            待处理: {{ pendingCount }}
          </el-tag>
          <el-tag
              type="success"
              size="medium"              style="margin-left: 8px"
              v-if="successCount > 0"
          >
            成功: {{ successCount }}
          </el-tag>
          <el-tag
              type="danger"
              size="medium"              style="margin-left: 8px"
              v-if="failedCount > 0"
          >
            失败: {{ failedCount }}
          </el-tag>
        </div>
      </div>

      <el-alert
          title="批量上传说明"
          type="info"
          :closable="false"
          show-icon          style="margin-bottom: 16px"
      >
        <div slot="default">
          <p style="margin: 4px 0">• 最多可选择 <strong>{{ maxSelectCount }} 个视频</strong></p>
          <p style="margin: 4px 0">• 系统将自动按每批 <strong>{{ batchSize }} 个视频</strong> 或约 {{ formatFileSize(batchMaxBytes) }} 分批提交</p>
          <p style="margin: 4px 0">• 最多同时提交 <strong>{{ batchSubmitConcurrency }} 批</strong>，后台队列控制实际上传并发</p>
          <p style="margin: 4px 0">• 上传过程中请勿关闭页面</p>
        </div>
      </el-alert>

      <!-- 文件夹选择视图（仅在没有默认类型时显示） -->
      <div v-if="!selectedFolder && !defaultUploadType" class="folder-view">
        <div class="folder-header">
          <h4><i class="el-icon-folder-opened"></i> 选择视频归属文件夹</h4>
          <p class="folder-hint">点击文件夹或直接将视频拖拽到文件夹上</p>
        </div>
        <div class="folder-grid">
          <div
              v-for="(folder, index) in folderList"
              :key="index"
              class="folder-item"
              :class="{ 'drag-over': dragOverFolder === folder }"
              @click="selectFolder(folder)"
              @dragover.prevent="handleDragOver($event, folder)"
              @dragleave="handleDragLeave($event, folder)"
              @drop.prevent="handleDropOnFolder($event, folder)"
          >
            <div class="folder-icon">
              <i class="el-icon-folder"></i>
            </div>
            <div class="folder-name">{{ folder }}</div>
            <div class="folder-drop-hint" v-if="dragOverFolder === folder">
              <i class="el-icon-upload2"></i>
              释放以上传
            </div>
          </div>
        </div>
      </div>

      <!-- 上传配置视图（点击文件夹后显示） -->
      <div v-else class="upload-config-view">
        <div class="config-header">
          <el-button
              type="text"
              icon="el-icon-back"
              @click="backToFolderView"
              class="back-btn"
          >
            返回文件夹列表
          </el-button>
          <el-tag type="success" size="medium">
            <i class="el-icon-folder-opened"></i>
            当前文件夹: {{ selectedFolder }}
          </el-tag>
        </div>

        <el-form :model="uploadConfig" label-width="120px" inline class="upload-form">
          <el-form-item label="广告主ID" required style="display: none" >
            <el-input
                readonly
                v-model="uploadConfig.advertiser_id"
                placeholder="请输入广告主ID"                style="width: 300px"

            />
          </el-form-item>

          <el-form-item label="视频类型">
            <el-tag type="success" size="medium">
              <i class="el-icon-folder-opened"></i>
              {{ selectedFolder }}
            </el-tag>
            <el-button
                v-if="!defaultUploadType"
                type="text"
                size="small"
                icon="el-icon-edit"
                @click="backToFolderView"                style="margin-left: 8px"
            >
              更改类型
            </el-button>
          </el-form-item>
          <el-form-item label="高级选项">
            <el-checkbox v-model="uploadConfig.flaw_detect">瑕疵检测</el-checkbox>
            <el-checkbox v-model="uploadConfig.auto_fix_enabled">自动修复</el-checkbox>
            <el-checkbox v-model="uploadConfig.auto_bind_enabled">自动绑定</el-checkbox>
          </el-form-item>
        </el-form>
      </div>

      <div class="upload-actions">
        <el-upload
            ref="videoUpload"
            class="video-uploader"
            action="#"
            :http-request="handleVideoSelect"
            :show-file-list="false"
            accept="video/mp4,video/quicktime"
            multiple
            :disabled="!uploadConfig.advertiser_id || (!selectedFolder && !defaultUploadType)"
        >
          <el-button
              type="primary"
              icon="el-icon-upload2"
              :disabled="!uploadConfig.advertiser_id || (!selectedFolder && !defaultUploadType)"
          >
            选择视频文件
          </el-button>
        </el-upload>

        <el-button
            type="success"
            icon="el-icon-upload"
            @click="submitBatchUpload"
            :disabled="!uploadConfig.advertiser_id || (!selectedFolder && !defaultUploadType) || getPendingCount() === 0 || isBatchUploading"
            :loading="isBatchUploading"
        >
          {{ isBatchUploading ? `上传中 (${currentBatchIndex}/${totalBatches})` : `开始上传 (${getPendingCount()})` }}
        </el-button>

        <el-button
            v-if="batchId && isUploading"
            type="info"
            icon="el-icon-refresh"
            @click="refreshBatchStatus"
            :loading="refreshing"
        >
          刷新状态
        </el-button>

        <el-button
            v-if="batchId && !isUploading"
            type="success"
            icon="el-icon-download"
            @click="exportResults"
        >
          导出结果
        </el-button>
      </div>
    </el-card>

    <!-- 待上传文件列表 -->
    <el-card
        shadow="never"
        class="pending-videos-card"
        v-if="getPendingCount() > 0"
    >
      <div slot="header">
        <span>
          <i class="el-icon-time"></i>
          待上传 ({{ getPendingCount() }})
        </span>
        <el-button
            type="text"
            size="small"            style="float: right; padding: 3px 0; color: #f56c6c"
            @click="clearAllPending"
        >
          <i class="el-icon-delete"></i> 清空全部
        </el-button>
      </div>

      <div class="pending-list">
        <div
            v-for="(video, index) in paginatedPendingVideos"
            :key="'pending-' + ((pendingCurrentPage - 1) * pendingPageSize + index)"
            class="pending-item"
        >
          <div class="item-info">
            <span class="file-name">{{ video.fileName }}</span>
            <i
                class="el-icon-delete delete-icon"
                @click="removePendingVideo(getRealPendingIndex(index))"
                title="删除此文件"
            ></i>
          </div>
        </div>
      </div>

      <div class="pagination-container" v-if="getPendingCount() > pendingPageSize">
        <el-pagination
            background
            layout="prev, pager, next, total"
            :current-page="pendingCurrentPage"
            :page-size="pendingPageSize"
            :total="getPendingCount()"
            @current-change="handlePendingPageChange"
        />
      </div>
    </el-card>

    <!-- 上传进度区 -->
    <el-card
        shadow="never"
        class="upload-progress-card"
        v-if="getUploadingAndCompletedCount() > 0"
    >
      <div slot="header">
        <span>
          <i class="el-icon-loading"></i>
          上传进度 ({{ getUploadingAndCompletedCount() }})
        </span>
      </div>

      <div class="uploading-list">
        <div
            v-for="(video, index) in paginatedUploadingAndCompletedVideos"
            :key="'uploading-' + ((uploadingCurrentPage - 1) * uploadingPageSize + index)"
            class="uploading-item"
        >
          <div class="item-info">
            <span class="file-name">{{ video.fileName }}</span>
            <el-tag size="mini" :type="getStatusTagType(video.status)">
              {{ getStatusText(video.status) }}
            </el-tag>
          </div>
          <el-progress
              :percentage="video.progress"
              :status="getProgressStatus(video.status)"
          />
          <div class="item-error" v-if="video.status === 'FAILED' && video.errorMessage">
            <i class="el-icon-warning"></i>
            {{ video.errorMessage }}
          </div>
        </div>
      </div>

      <div class="pagination-container" v-if="getUploadingAndCompletedCount() > uploadingPageSize">
        <el-pagination
            background
            layout="prev, pager, next, total"
            :current-page="uploadingCurrentPage"
            :page-size="uploadingPageSize"
            :total="getUploadingAndCompletedCount()"
            @current-change="handleUploadingPageChange"
        />
      </div>
    </el-card>

    <!-- 上传结果区 -->
    <el-card
        shadow="never"
        class="upload-results-card"
        v-if="completedVideos.length > 0"
    >
      <div slot="header">
        <span>
          <i class="el-icon-circle-check"></i>
          上传结果 ({{ completedVideos.length }})
        </span>
      </div>

      <el-table
          :data="paginatedCompletedVideos"
          border
          stripe
          max-height="400"
      >
        <el-table-column
            prop="fileName"
            label="文件名"
            min-width="120"
            show-overflow-tooltip
        />

        <el-table-column
            label="状态"
            width="120"
        >
          <template slot-scope="{ row }">
            <el-tag :type="getResultTagType(row.status)" size="small">
              {{ getResultText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
            prop="videoId"
            label="视频ID"
            width="320"
            v-if="hasVideoId"
        >
          <template slot-scope="{ row }">
            <div class="id-cell" v-if="row.videoId">
              <span>{{ row.videoId }}</span>
              <i
                  class="el-icon-document-copy copy-icon"
                  @click="copyToClipboard(row.videoId)"
              ></i>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <el-table-column
            prop="materialId"
            label="素材ID"
            width="260"
            v-if="hasMaterialId"
        >
          <template slot-scope="{ row }">
            <div class="id-cell" v-if="row.materialId">
              <span>{{ row.materialId }}</span>
              <i
                  class="el-icon-document-copy copy-icon"
                  @click="copyToClipboard(row.materialId)"
              ></i>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <el-table-column
            label="错误信息"
            min-width="200"
            show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <span class="error-text" v-if="row.errorMessage">
              {{ row.errorMessage }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container" v-if="completedVideos.length > resultPageSize">
        <el-pagination
            background
            layout="prev, pager, next, total"
            :current-page="resultCurrentPage"
            :page-size="resultPageSize"
            :total="completedVideos.length"
            @current-change="handleResultPageChange"
        />
      </div>
    </el-card>

    <!-- 空状态提示 -->
    <el-empty
        v-if="!batchId && uploadingVideos.length === 0 && completedVideos.length === 0"
        description="暂无上传任务，请选择视频文件开始上传"
        :image-size="120"
    >
      <el-button
          type="primary"
          @click="$refs.videoUpload.$el.querySelector('input').click()"
          :disabled="!uploadConfig.advertiser_id || !selectedFolder"
      >
        立即上传
      </el-button>
    </el-empty>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'VideoUploadComponent',

  props: {
    baseUrl: {
      type: String,
      // default: 'https://tiktoktf.darknight.games/api/api'
      default: 'https://tiktoktf.darknight.games/api/api'
    },

    defaultAdvertiserId: {
      type: String,
      default: ''
    },
    defaultUploadType: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      uploadConfig: {
        advertiser_id: this.defaultAdvertiserId,
        flaw_detect: true,
        auto_fix_enabled: true,
        auto_bind_enabled: true,
        videoOwner: ''
      },

      selectedFolder: this.defaultUploadType || null,


      folderList: [],

      dragOverFolder: null,

      batchId: null,
      batchIds: [],
      uploadingVideos: [],
      completedVideos: [],

      refreshing: false,
      pollTimer: null,
      pollInterval: 3000,

      maxSelectCount: 300,
      batchSize: 10,
      batchMaxBytes: 850 * 1024 * 1024,
      batchSubmitConcurrency: 3,
      isBatchUploading: false,
      currentBatchIndex: 0,
      totalBatches: 0,

      currentPage: 1,
      pageSize: 10,

      resultCurrentPage: 1,
      resultPageSize: 10,

      pendingCurrentPage: 1,
      pendingPageSize: 10,

      uploadingCurrentPage: 1,
      uploadingPageSize: 10
    }
  },
  computed: {
    totalVideos() {
      return this.uploadingVideos.length + this.completedVideos.length
    },

    pendingCount() {
      return this.uploadingVideos.filter(v =>
          v.status === 'PENDING' || v.status === 'UPLOADING'
      ).length
    },

    successCount() {
      return this.completedVideos.filter(v => v.status === 'SUCCESS').length
    },

    failedCount() {
      return this.completedVideos.filter(v => v.status === 'FAILED').length +
          this.uploadingVideos.filter(v => v.status === 'FAILED').length
    },

    isUploading() {
      return this.pendingCount > 0
    },

    hasVideoId() {
      return this.completedVideos.some(v => v.videoId)
    },

    hasMaterialId() {
      return this.completedVideos.some(v => v.materialId)
    },

    paginatedUploadingVideos() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.uploadingVideos.slice(start, end)
    },

    paginatedCompletedVideos() {
      const start = (this.resultCurrentPage - 1) * this.resultPageSize
      const end = start + this.resultPageSize
      return this.completedVideos.slice(start, end)
    },

    paginatedPendingVideos() {
      const pendingVideos = this.uploadingVideos.filter(v => v.status === 'PENDING')
      const start = (this.pendingCurrentPage - 1) * this.pendingPageSize
      const end = start + this.pendingPageSize
      return pendingVideos.slice(start, end)
    },

    paginatedUploadingAndCompletedVideos() {
      const videos = this.uploadingVideos.filter(v => v.status !== 'PENDING')
      const start = (this.uploadingCurrentPage - 1) * this.uploadingPageSize
      const end = start + this.uploadingPageSize
      return videos.slice(start, end)
    }
  },

  watch: {
    defaultAdvertiserId(val) {
      if (val) {
        this.uploadConfig.advertiser_id = val
      }
    },
    defaultUploadType(val) {
      if (val) {
        this.selectedFolder = val
      }
    }
  },

  beforeDestroy() {
    this.stopPolling()
  },
  created() {
    this.loadFolderList()
  },
  methods: { async loadFolderList() {
      try {
        const response = await axios.get(
            `${this.baseUrl}/upload-folder/list`,
            {
              withCredentials: false,
              timeout: 10000
            }
        )

        if (response.data.code === 200 || response.data.code === 0) {
          const folders = response.data.data || []

          this.folderList = folders.map(folder => folder.folderName)
        } else {
          throw new Error(response.data.message || '获取文件夹列表失败')
        }
      } catch (error) {
        console.error('加载文件夹列表失败', error)
        this.folderList = [
          '找茬',
          '脑洞',
          '拼图',
          '麻将',
          '数独'
        ]
      }
    },
    async handleVideoSelect(options) {
      const file = options.file

      const isVideo = file.type === 'video/mp4' || file.type === 'video/quicktime'
      if (!isVideo) {
        this.$message.error(`文件 "${file.name}" 格式不正确，只能上传 MP4 或 MOV 格式`)
        return
      }

      const isLt500M = file.size / 1024 / 1024 < 500
      if (!isLt500M) {
        this.$message.error(`文件 "${file.name}" 大小超过 500MB`)
        return
      }

      const pendingCount = this.uploadingVideos.filter(v => v.status === 'PENDING').length
      if (pendingCount >= this.maxSelectCount) {
        this.$message.error(`最多只能选择 ${this.maxSelectCount} 个视频`)
        return
      }

      const isDuplicate = this.uploadingVideos.some(v =>
          v.fileName === file.name && v.status === 'PENDING'
      )

      if (isDuplicate) {
        this.$message.warning(`文件 "${file.name}" 已在上传队列中，已跳过`)
        return
      }

      this.uploadingVideos.push({
        fileName: file.name,
        file: file,
        status: 'PENDING',
        progress: 0,
        videoId: '',
        materialId: '',
        errorMessage: ''
      })

      this.$message.success(`已将 "${file.name}" 加入上传队列 (${pendingCount + 1}/${this.maxSelectCount})`)
    },

    async submitBatchUpload() {
      if (!this.uploadConfig.advertiser_id) {
        this.$message.error('请先输入广告主ID')
        return
      }

      if (!this.selectedFolder) {
        this.$message.error('请先选择视频归属文件夹')
        return
      }

      const pendingVideos = this.uploadingVideos.filter(v => v.status === 'PENDING')
      if (pendingVideos.length === 0) {
        this.$message.warning('没有待上传的视频')
        return
      }

      if (pendingVideos.length > this.maxSelectCount) {
        this.$message.error(`最多支持 ${this.maxSelectCount} 个视频`)
        return
      }

      this.isBatchUploading = true
      this.currentBatchIndex = 0
      const batches = this.createUploadBatches(pendingVideos)
      this.totalBatches = batches.length
      this.currentPage = 1
      this.batchIds = []

      this.$message.info(`共 ${pendingVideos.length} 个视频，将分 ${this.totalBatches} 批提交，最多 ${this.batchSubmitConcurrency} 批并行`)

      try {
        const failedBatches = await this.uploadBatchesWithConcurrency(batches)
        const submittedBatches = this.totalBatches - failedBatches.length

        if (submittedBatches > 0) {
          this.$message.success(`已提交 ${submittedBatches}/${this.totalBatches} 批，后台继续上传处理`)
          this.startPolling()
        }

        if (failedBatches.length > 0) {
          this.$message.warning(`${failedBatches.length} 批提交失败，可检查失败原因后重试`)
        }
      } catch (error) {
        console.error('批量上传失败', error)
        this.$message.error('批量上传失败: ' + error.message)
      } finally {
        this.isBatchUploading = false
        this.currentBatchIndex = 0
        this.totalBatches = 0
      }
    },

    createUploadBatches(videos) {
      const batches = []
      let currentBatch = []
      let currentSize = 0

      videos.forEach(video => {
        const fileSize = video.file && video.file.size ? video.file.size : 0
        const reachesCountLimit = currentBatch.length >= this.batchSize
        const reachesSizeLimit = currentBatch.length > 0 && currentSize + fileSize > this.batchMaxBytes

        if (reachesCountLimit || reachesSizeLimit) {
          batches.push(currentBatch)
          currentBatch = []
          currentSize = 0
        }

        currentBatch.push(video)
        currentSize += fileSize
      })

      if (currentBatch.length > 0) {
        batches.push(currentBatch)
      }

      return batches
    },

    async uploadBatchesWithConcurrency(batches) {
      const failedBatches = []
      let nextIndex = 0
      let completedCount = 0
      const workerCount = Math.min(this.batchSubmitConcurrency, batches.length)

      const runWorker = async () => {
        while (nextIndex < batches.length) {
          const batchIndex = nextIndex++
          const batch = batches[batchIndex]
          this.currentBatchIndex = batchIndex + 1
          this.$message.info(`正在提交第 ${batchIndex + 1}/${batches.length} 批（${batch.length} 个视频）...`)

          try {
            await this.uploadSingleBatch(batch, batchIndex + 1)
          } catch (error) {
            failedBatches.push({ index: batchIndex + 1, error })
          } finally {
            completedCount += 1
            this.currentBatchIndex = completedCount
          }
        }
      }

      await Promise.all(Array.from({ length: workerCount }, runWorker))
      return failedBatches
    },

    async uploadSingleBatch(batchVideos, batchIndex) {
      const formData = new FormData()
      formData.append('advertiser_id', this.uploadConfig.advertiser_id)

      batchVideos.forEach((video) => {
        if (video.file) {
          formData.append('video_files', video.file)
          formData.append('file_names', video.fileName)
        }
      })

      formData.append('flaw_detect', this.uploadConfig.flaw_detect)
      formData.append('auto_fix_enabled', this.uploadConfig.auto_fix_enabled)
      formData.append('auto_bind_enabled', this.uploadConfig.auto_bind_enabled)
      formData.append('upload_person', this.$store.state.username || this.$store.state.user?.username || '未登录')
      if (this.selectedFolder) {
        formData.append('upload_video_type', this.selectedFolder)
      }
      // if (this.uploadConfig.videoOwner) {
      //   formData.append('upload_video_type', this.uploadConfig.videoOwner)
      // }
      batchVideos.forEach(video => {
        video.status = 'UPLOADING'
        video.progress = 10
      })

      try {
        const response = await axios.post(
            `${this.baseUrl}/tiktok/video/v2/upload/batch`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              withCredentials: false,
              timeout: 300000
            }
        )

        if (response.data.code === 200 || response.data.code === 0) {
          const batchId = response.data.data.batchId
          this.batchIds.push(batchId)

          if (!this.batchId) {
            this.batchId = batchId
          }

          const results = response.data.data.results || []
          results.forEach((result, index) => {
            if (index < batchVideos.length) {
              batchVideos[index].status = result.status
              batchVideos[index].progress = result.status === 'PENDING' ? 30 : 0
            }
          })

          this.$message.success(`第 ${batchIndex} 批已提交，正在后台处理...`)
        } else {
          throw new Error(response.data.message || '上传失败')
        }
      } catch (error) {
        console.error(`第 ${batchIndex} 批上传失败`, error)
        batchVideos.forEach(video => {
          video.status = 'FAILED'
          video.errorMessage = error.message || '网络请求失败'
          video.progress = 0
        })
        throw error
      }
    },

    formatFileSize(bytes) {
      if (!bytes) {
        return '0MB'
      }
      const mb = bytes / 1024 / 1024
      if (mb >= 1024) {
        return `${(mb / 1024).toFixed(1)}GB`
      }
      return `${Math.round(mb)}MB`
    },

    async refreshBatchStatus() {
      if (this.batchIds.length === 0) return

      this.refreshing = true

      try {
        const responses = await Promise.all(this.batchIds.map(batchId =>
          axios.get(
              `${this.baseUrl}/tiktok/video/v2/upload/batch/${batchId}`,
              {
                withCredentials: false,
                timeout: 30000
              }
          ).catch(error => ({ error, batchId }))
        ))

        let hasQueryError = false
        responses.forEach(response => {
          if (response.error) {
            hasQueryError = true
            console.error(`查询批次 ${response.batchId} 状态失败`, response.error)
            return
          }
          if (response.data.code === 200 || response.data.code === 0) {
            const batchData = response.data.data
            const results = batchData.results || []

            results.forEach(result => {
              let video = this.uploadingVideos.find(v => v.fileName === result.fileName)

              if (!video) {
                video = this.completedVideos.find(v => v.fileName === result.fileName)
              }

              if (video) {
                video.status = result.status
                video.videoId = result.videoId || ''
                video.materialId = result.materialId || ''
                video.errorMessage = result.errorMessage || ''

                if (result.status === 'SUCCESS') {
                  video.progress = 100
                } else if (result.status === 'FAILED') {
                  video.progress = 0
                } else if (result.status === 'PENDING' || result.status === 'UPLOADING') {
                  video.progress = 50
                }

                if (['SUCCESS', 'FAILED'].includes(result.status)) {
                  if (!this.completedVideos.find(v => v.fileName === result.fileName)) {
                    this.completedVideos.push(video)
                    const index = this.uploadingVideos.findIndex(v => v.fileName === result.fileName)
                    if (index !== -1) {
                      this.uploadingVideos.splice(index, 1)
                    }
                  }
                }
              }
            })
          }
        })

        if (!this.isUploading) {
          this.stopPolling()
          this.$message.success('所有视频上传完成！')
        } else if (hasQueryError) {
          this.$message.warning('部分批次状态查询失败，稍后会继续刷新')
        }
      } catch (error) {
        console.error('查询上传状态失败', error)
        this.$message.warning('查询上传状态失败')
      } finally {
        this.refreshing = false
      }
    },

    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(() => {
        this.refreshBatchStatus()
      }, this.pollInterval)
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    getStatusTagType(status) {
      const types = {
        'PENDING': 'info',
        'UPLOADING': 'warning',
        'SUCCESS': 'success',
        'FAILED': 'danger',
        'SKIPPED': 'info'
      }
      return types[status] || 'info'
    },

    getPendingCount() {
      return this.uploadingVideos.filter(v => v.status === 'PENDING').length
    },

    getStatusText(status) {
      const texts = {
        'PENDING': '等待中',
        'UPLOADING': '上传中',
        'SUCCESS': '成功',
        'FAILED': '失败',
        'SKIPPED': '已跳过'
      }
      return texts[status] || status
    },

    getProgressStatus(status) {
      if (status === 'SUCCESS') return 'success'
      if (status === 'FAILED') return 'exception'
      return null
    },

    getResultTagType(status) {
      const types = {
        'SUCCESS': 'success',
        'FAILED': 'danger',
        'PENDING': 'info',
        'UPLOADING': 'warning'
      }
      return types[status] || 'info'
    },

    getResultText(status) {
      return this.getStatusText(status)
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制到剪贴板')
      }).catch(() => {
        this.$message.error('复制失败')
      })
    },

    exportResults() {
      if (this.completedVideos.length === 0) {
        this.$message.warning('暂无结果可导出')
        return
      }

      const headers = ['文件名', '状态', '视频ID', '素材ID', '错误信息']
      const rows = this.completedVideos.map(v => [
        v.fileName,
        this.getResultText(v.status),
        v.videoId || '',
        v.materialId || '',
        v.errorMessage || ''
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n')

      const blob = new Blob(['\ufeff' + csvContent], {type: 'text/csv;charset=utf-8;'})
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `视频上传结果_${new Date().getTime()}.csv`
      link.click()

      this.$message.success('导出成功')
    },

    handlePageChange(page) {
      this.currentPage = page
    },

    handleResultPageChange(page) {
      this.resultCurrentPage = page
    },

    selectFolder(folder) {
      this.selectedFolder = folder
      this.uploadConfig.videoOwner = folder
    },

    backToFolderView() {
      this.selectedFolder = null
      this.uploadConfig.videoOwner = ''
    }, handlePendingPageChange(page) {
      this.pendingCurrentPage = page
    },

    handleUploadingPageChange(page) {
      this.uploadingCurrentPage = page
    },

    getRealPendingIndex(displayIndex) {
      const pendingVideos = this.uploadingVideos.filter(v => v.status === 'PENDING')
      const video = pendingVideos[displayIndex]
      return this.uploadingVideos.findIndex(v => v === video)
    },

    getUploadingAndCompletedCount() {
      return this.uploadingVideos.filter(v => v.status !== 'PENDING').length
    },

    clearAllPending() {
      this.$confirm('确定要清空所有待上传文件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.uploadingVideos = this.uploadingVideos.filter(v => v.status !== 'PENDING')
        this.pendingCurrentPage = 1
        this.$message.success('已清空所有待上传文件')
      }).catch(() => {})
    },





    queryOwnerSuggestions(queryString, cb) {
      const ownerList = [
        {value: '找茬'},
        {value: '脑洞'},
        {value: '拼图'},
        {value: '麻将'},
        {value: '数独'}
      ]

      const results = queryString
          ? ownerList.filter(item =>
              item.value.toLowerCase().includes(queryString.toLowerCase())
          )
          : ownerList

      cb(results)

    },
    handleDragOver(event, folder) {
      if (!this.uploadConfig.advertiser_id) {
        this.$message.warning('请先输入广告主ID')
        return
      }
      this.dragOverFolder = folder
    },

    handleDragLeave(event, folder) {
      this.dragOverFolder = null
    },

    async handleDropOnFolder(event, folder) {
      this.dragOverFolder = null

      if (!this.uploadConfig.advertiser_id) {
        this.$message.error('请先输入广告主ID')
        return
      }

      const files = Array.from(event.dataTransfer.files)

      if (files.length === 0) {
        return
      }

      const pendingCount = this.uploadingVideos.filter(v => v.status === 'PENDING').length
      const availableSlots = this.maxSelectCount - pendingCount

      if (availableSlots <= 0) {
        this.$message.error(`最多只能选择 ${this.maxSelectCount} 个视频`)
        return
      }

      let addedCount = 0
      let skippedCount = 0
      let errorCount = 0

      for (const file of files) {
        if (addedCount >= availableSlots) {
          this.$message.warning(`已达到最大数量限制，剩余 ${files.length - addedCount - skippedCount - errorCount} 个文件未添加`)
          break
        }

        const isVideo = file.type === 'video/mp4' || file.type === 'video/quicktime'
        if (!isVideo) {
          errorCount++
          continue
        }

        const isLt500M = file.size / 1024 / 1024 < 500
        if (!isLt500M) {
          errorCount++
          continue
        }

        const isDuplicate = this.uploadingVideos.some(v =>
            v.fileName === file.name && v.status === 'PENDING'
        )

        if (isDuplicate) {
          skippedCount++
          continue
        }

        this.uploadingVideos.push({
          fileName: file.name,
          file: file,
          status: 'PENDING',
          progress: 0,
          videoId: '',
          materialId: '',
          errorMessage: ''
        })
        addedCount++
      }

      if (addedCount > 0) {
        this.selectFolder(folder)

        let message = `已将 ${addedCount} 个视频添加到 "${folder}" 文件夹`
        if (skippedCount > 0) {
          message += `，跳过 ${skippedCount} 个重复文件`
        }
        if (errorCount > 0) {
          message += `，${errorCount} 个文件格式不符或过大`
        }
        message += ` (${this.getPendingCount()}/${this.maxSelectCount})`

        this.$message.success(message)
      } else if (skippedCount > 0 || errorCount > 0) {
        let message = '没有添加新视频'
        if (skippedCount > 0) {
          message += `，${skippedCount} 个文件已存在`
        }
        if (errorCount > 0) {
          message += `，${errorCount} 个文件格式不符或过大`
        }
        this.$message.warning(message)
      }
    },
    removePendingVideo(index) {
      const video = this.uploadingVideos[index]

      if (!video || video.status !== 'PENDING') {
        return
      }

      this.$confirm(`确定要删除文件 "${video.fileName}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.uploadingVideos.splice(index, 1)
        this.$message.success('已删除')

        const newPendingCount = this.getPendingCount()
        if (newPendingCount === 0) {
          this.pendingCurrentPage = 1
        } else if (this.pendingCurrentPage > Math.ceil(newPendingCount / this.pendingPageSize)) {
          this.pendingCurrentPage = Math.ceil(newPendingCount / this.pendingPageSize)
        }
      }).catch(() => {})
    },
  }
}
</script>

<style scoped>.video-upload-component {
  .upload-control-card {
    margin-bottom: 20px;
  }

  .upload-control-card .upload-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  .upload-control-card .upload-header h3 {
    margin: 0;
    font-size: 18px;
    color: #303133;
  }

  .upload-control-card .upload-header h3 i {
    margin-right: 8px;
    color: #409EFF;
  }

  .upload-control-card .upload-stats {
    display: flex;
    align-items: center;
  }

  /* 文件夹视图样式 */
  .folder-view {
    margin-bottom: 16px;
  }

  .folder-view .folder-header {
    margin-bottom: 16px;
    padding: 12px;
    background: #f5f7fa;
    border-radius: 4px;
  }

  .folder-view .folder-header h4 {
    margin: 0;
    font-size: 16px;
    color: #303133;
  }
  .folder-view .folder-header .folder-hint {
    margin: 8px 0 0 0;
    font-size: 13px;
    color: #909399;
  }

  .folder-view .folder-header h4 i {
    margin-right: 8px;
    color: #409EFF;
  }
  .folder-view .folder-header .folder-hint {
    margin: 8px 0 0 0;
    font-size: 13px;
    color: #909399;
  }

  .folder-view .folder-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 16px;
    padding: 8px;
  }

  .folder-view .folder-grid .folder-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    background: #fff;
    border: 2px solid #e4e7ed;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    user-select: none;
  }

  .folder-view .folder-grid .folder-item:hover {
    border-color: #409EFF;
    background-color: #ecf5ff;
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.25);
  }

  .folder-view .folder-grid .folder-item .folder-icon {
    font-size: 48px;
    color: #409EFF;
    margin-bottom: 12px;
    transition: all 0.3s ease;
  }

  .folder-view .folder-grid .folder-item:hover .folder-icon {
    transform: scale(1.1);
  }

  .folder-view .folder-grid .folder-item .folder-name {
    font-size: 14px;
    font-weight: 500;
    color: #303133;
    text-align: center;
  }

  /* 上传配置视图样式 */
  .upload-config-view {
    margin-bottom: 16px;
  }

  .upload-config-view .config-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
    padding: 12px;
    background: #f5f7fa;
    border-radius: 4px;
  }

  .upload-config-view .config-header .back-btn {
    padding: 0;
    font-size: 14px;
  }

  .upload-config-view .upload-form {
    background: #fff;
    padding: 16px;
    border-radius: 4px;
  }

  .upload-control-card .upload-config {
    margin-bottom: 16px;
  }

  .upload-control-card .upload-actions {
    display: flex !important;
    gap: 12px;
    align-items: center;
    flex-wrap: nowrap;
  }

  .upload-control-card .upload-actions .video-uploader-inline {
    display: inline-block;
  }

  .upload-control-card .upload-actions .el-button {
    white-space: nowrap;
  }
  /* 待上传文件列表样式 */
  .pending-videos-card {
    margin-bottom: 20px;
  }

  .pending-videos-card .pending-list .pending-item {
    margin-bottom: 8px;
    padding: 10px 12px;
    background: #f5f7fa;
    border-radius: 4px;
    transition: all 0.3s ease;
  }

  .pending-videos-card .pending-list .pending-item:hover {
    background: #e8eaed;
  }

  .pending-videos-card .pending-list .pending-item:last-child {
    margin-bottom: 0;
  }

  .pending-videos-card .pending-list .pending-item .item-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .pending-videos-card .pending-list .pending-item .item-info .file-name {
    font-weight: 500;
    color: #303133;
    flex: 1;
    margin-right: 12px;
  }

  .pending-videos-card .pending-list .pending-item .item-info .delete-icon {
    cursor: pointer;
    color: #f56c6c;
    font-size: 16px;
    padding: 4px;
    transition: all 0.3s ease;
  }

  .pending-videos-card .pending-list .pending-item .item-info .delete-icon:hover {
    color: #ff0000;
    transform: scale(1.2);
  }

  .pending-videos-card .pagination-container {
    margin-top: 16px;
    text-align: center;
  }

  .upload-progress-card {
    margin-bottom: 20px;
  }

  .upload-progress-card .uploading-list .uploading-item {
    margin-bottom: 16px;
    padding: 12px;
    background: #f5f7fa;
    border-radius: 4px;
  }

  .upload-progress-card .uploading-list .uploading-item:last-child {
    margin-bottom: 0;
  }

  .upload-progress-card .uploading-list .uploading-item .item-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .upload-progress-card .uploading-list .uploading-item .item-info .file-name {
    font-weight: 500;
    color: #303133;
  }

  .upload-progress-card .uploading-list .uploading-item .item-error {
    margin-top: 8px;
    color: #f56c6c;
    font-size: 12px;
  }

  .upload-progress-card .uploading-list .uploading-item .item-error i {
    margin-right: 4px;
  }

  .upload-results-card .id-cell {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .upload-results-card .id-cell .copy-icon {
    cursor: pointer;
    color: #409EFF;
    font-size: 14px;
  }

  .upload-results-card .id-cell .copy-icon:hover {
    color: #66b1ff;
  }

  .upload-results-card .error-text {
    color: #f56c6c;
  }

  .upload-progress-card .pagination-container {
    margin-top: 20px;
    text-align: center;
  }
}




.folder-view .folder-grid .folder-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: #fff;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
  position: relative;
  min-height: 140px;
}
.folder-view .folder-grid .folder-item:hover .folder-icon {
  transform: scale(1.1);
}

.folder-view .folder-grid .folder-item.drag-over {
  border-color: #67c23a;
  background-color: #f0f9ff;
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(103, 194, 58, 0.3);
}

.folder-view .folder-grid .folder-item.drag-over .folder-icon {
  color: #67c23a;
  animation: bounce 0.6s ease infinite alternate;
}

@keyframes bounce {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-8px);
  }
}

.folder-view .folder-grid .folder-item .folder-drop-hint {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: #67c23a;
  font-weight: 500;
  white-space: nowrap;
  animation: fadeIn 0.3s ease;
}

.folder-view .folder-grid .folder-item .folder-drop-hint i {
  margin-right: 4px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}
</style>
